/**
 * SAAPlanner — Hoạch định Cơ cấu Tài sản Chiến lược (Strategic Asset Allocation)
 *
 * Export:
 *   - TargetRow       (kiểu dữ liệu UI-state)
 *   - SAAWorkspace    (Tab "Hoạch định" — nhập liệu, FIRE projection, lưu)
 *   - SAAComparison   (Tab "Tổng quan" — đối soát Actual vs Target)
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Plus, Trash2, Save, AlertTriangle, CheckCircle,
  BarChart2, TrendingUp, Target, Layers, X,
  Bookmark, BookOpen, ChevronDown, ChevronUp, Zap, Copy, Download,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import { AssetClassMeta, Bucket, Asset, FundTargetRow } from '../types';
import { formatVND } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';
import { saveFundTargets, getSessionUserId, isUUID } from '../utils/storage';
import { v4 } from '../utils/uuid';
import { getAssetCurrentValue } from '../utils/calculations';
import { Card } from './ui/Card';
import { AIAppraisalButton } from './AIAppraisal';

// ── SAA Scenarios (Phiên bản hoạch định lưu localStorage) ────────────────────
export interface SAAScenario {
  id:        string;
  fundId:    string;
  name:      string;
  note:      string;
  refNAV:    number;
  targets:   TargetRow[];
  isActive:  boolean;
  createdAt: string; // ISO
}

function scenarioKey(fundId: string) { return `wealthos:saa:scenarios:${fundId}`; }

function loadScenarios(fundId: string): SAAScenario[] {
  try { const r = localStorage.getItem(scenarioKey(fundId)); return r ? JSON.parse(r) : []; }
  catch { return []; }
}

function persistScenarios(fundId: string, list: SAAScenario[]) {
  try { localStorage.setItem(scenarioKey(fundId), JSON.stringify(list)); } catch { /* quota */ }
}

// ── Kiểu dữ liệu UI (local, khác FundTargetRow là DB shape) ─────────────────
export interface TargetRow {
  assetClassId:    string;
  assetClassName:  string;
  assetClassColor: string;
  weight:   number;   // % tỷ trọng (0–100)
  value:    number;   // VND tuyệt đối
  expYield: number;   // % yield kỳ vọng / năm
  expCap:   number;   // % cap gain kỳ vọng / năm
}

// ── Tooltip style chung ───────────────────────────────────────────────────────
const TT = {
  contentStyle: { backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-bd)', borderRadius: 10, fontSize: 11, color: 'var(--tx-1)' },
  itemStyle:    { color: 'var(--tx-3)' },
  cursor:       { stroke: 'var(--tx-5)', strokeDasharray: '3 3' },
};

// ── Định dạng trục Y cho biểu đồ ─────────────────────────────────────────────
function fmtAxis(v: number): string {
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}N`;
  if (v >= 1e9)  return `${(v / 1e9).toFixed(1)}T`;
  if (v >= 1e6)  return `${(v / 1e6).toFixed(0)}M`;
  return String(v);
}

// ══════════════════════════════════════════════════════════════════════════════
// SAAWorkspace — Tab "Hoạch định"
// ══════════════════════════════════════════════════════════════════════════════
export function SAAWorkspace({
  fundId, bucket, assetClasses,
  targets, setTargets,
  refNAV: refNAVProp,
  fundNAV,
  onSaved,
  onAddAssetClass,
}: {
  fundId:            string;
  bucket:            Bucket;
  assetClasses:      AssetClassMeta[];
  targets:           TargetRow[];
  setTargets:        (t: TargetRow[]) => void;
  refNAV:            number;   // NAV tham chiếu ban đầu (bucket.targetValue hoặc totalValue)
  fundNAV:           number;   // NAV thực tế của quỹ (dùng cho FIRE projection)
  onSaved:           (msg: string) => void;
  onAddAssetClass?:  (cls: AssetClassMeta) => void;
}) {
  const [saving,         setSaving]         = useState(false);
  const [selCls,         setSelCls]         = useState('');
  const [localRefNAV,    setLocalRefNAV]    = useState(refNAVProp);
  const [fireWithdrawal, setFireWithdrawal] = useState(1_200_000_000); // 1.2 tỷ VND
  const [fireInflation,  setFireInflation]  = useState(4);            // 4%/năm
  const [showNewCls,     setShowNewCls]     = useState(false);
  const [newClsName,     setNewClsName]     = useState('');
  const [newClsColor,    setNewClsColor]    = useState('#6366f1');
  const newClsInputRef = useRef<HTMLInputElement>(null);

  // ── Scenarios state ────────────────────────────────────────────────────────
  const [scenarios,      setScenarios]      = useState<SAAScenario[]>(() => loadScenarios(fundId));
  const [showSaveModal,  setShowSaveModal]  = useState(false);
  const [scenarioName,   setScenarioName]   = useState('');
  const [scenarioNote,   setScenarioNote]   = useState('');
  const [showScenarios,  setShowScenarios]  = useState(false);
  const [showCompare,    setShowCompare]    = useState(false);

  // Đồng bộ khi prop thay đổi (ví dụ: user cập nhật bucket.targetValue)
  useEffect(() => { setLocalRefNAV(refNAVProp); }, [refNAVProp]);

  // Lớp tài sản chưa có trong bảng
  const available = assetClasses.filter(c => !targets.some(t => t.assetClassId === c.id));

  // ── Thêm dòng ──────────────────────────────────────────────────────────────
  const addRow = () => {
    if (!selCls) return;
    const cls = assetClasses.find(c => c.id === selCls);
    if (!cls) return;

    let effectiveId = cls.id;
    // Slug class không sync được lên DB — promote sang UUID
    if (!isUUID(cls.id) && onAddAssetClass) {
      const existing = assetClasses.find(c => isUUID(c.id) && c.name === cls.name);
      if (existing) {
        effectiveId = existing.id;
      } else {
        effectiveId = v4();
        onAddAssetClass({ ...cls, id: effectiveId });
      }
    }

    setTargets([...targets, {
      assetClassId: effectiveId, assetClassName: cls.name, assetClassColor: cls.color,
      weight: 0, value: 0, expYield: 0, expCap: 0,
    }]);
    setSelCls('');
  };

  // ── Xóa dòng ───────────────────────────────────────────────────────────────
  const removeRow = (idx: number) => setTargets(targets.filter((_, i) => i !== idx));

  // ── Tạo lớp mới inline ─────────────────────────────────────────────────────
  const handleCreateClass = () => {
    const name = newClsName.trim();
    if (!name || !onAddAssetClass) return;
    // Luôn dùng UUID để class sync được lên DB (slug không pass isUUID check trong storage.ts)
    const cls: AssetClassMeta = { id: v4(), name, color: newClsColor };
    onAddAssetClass(cls);
    // Auto-add to SAA table
    setTargets([...targets, {
      assetClassId: cls.id, assetClassName: cls.name, assetClassColor: cls.color,
      weight: 0, value: 0, expYield: 0, expCap: 0,
    }]);
    setNewClsName(''); setNewClsColor('#6366f1'); setShowNewCls(false);
  };

  // ── Thay đổi NAV tham chiếu → giữ % trọng số, tính lại VND ────────────────
  const handleRefNAVChange = (newNAV: number) => {
    setLocalRefNAV(newNAV);
    setTargets(targets.map(t => ({
      ...t,
      value: newNAV > 0 ? (t.weight / 100) * newNAV : t.value,
    })));
  };

  // ── % tỷ trọng → tính VND ──────────────────────────────────────────────────
  const updateWeight = (idx: number, w: number) => {
    const v = localRefNAV > 0 ? (w / 100) * localRefNAV : 0;
    setTargets(targets.map((t, i) => i === idx ? { ...t, weight: w, value: v } : t));
  };

  // ── VND → tính % ───────────────────────────────────────────────────────────
  const updateValue = (idx: number, raw: string) => {
    const v = Number(raw.replace(/[^0-9]/g, '')) || 0;
    const w = localRefNAV > 0 ? (v / localRefNAV) * 100 : 0;
    setTargets(targets.map((t, i) => i === idx ? { ...t, value: v, weight: w } : t));
  };

  const updateField = (idx: number, field: 'expYield' | 'expCap', v: number) =>
    setTargets(targets.map((t, i) => i === idx ? { ...t, [field]: v } : t));

  // ── Tổng hợp ───────────────────────────────────────────────────────────────
  const totalWeight  = targets.reduce((s, t) => s + t.weight, 0);
  const totalValue   = targets.reduce((s, t) => s + t.value,  0);
  const blendedYield = targets.reduce((s, t) => s + (t.weight / 100) * t.expYield, 0);
  const blendedCap   = targets.reduce((s, t) => s + (t.weight / 100) * t.expCap,   0);
  const blendedTotal = blendedYield + blendedCap;
  const weightOk     = targets.length === 0 || Math.abs(totalWeight - 100) < 0.5;

  // ── FIRE 30-năm projection ──────────────────────────────────────────────────
  // Dùng localRefNAV (Target NAV chiến lược) làm điểm khởi đầu,
  // KHÔNG dùng fundNAV (NAV thực tế ròng) — mô phỏng phải dựa trên mục tiêu thiết kế.
  const fireData = useMemo(() => {
    if (localRefNAV <= 0 || blendedTotal <= 0) return [];
    const rows: { year: number; open: number; gain: number; withdrawal: number; close: number }[] = [];
    let open = localRefNAV;
    for (let y = 1; y <= 30; y++) {
      const gain       = open * (blendedTotal / 100);
      const withdrawal = fireWithdrawal * Math.pow(1 + fireInflation / 100, y - 1);
      const close      = Math.max(open + gain - withdrawal, 0);
      rows.push({ year: y, open, gain, withdrawal, close });
      open = close;
      if (close === 0) break;
    }
    return rows;
  }, [localRefNAV, blendedTotal, fireWithdrawal, fireInflation]);

  const fireChartData = useMemo(() => [
    { year: 0, nav: localRefNAV },
    ...fireData.map(r => ({ year: r.year, nav: r.close })),
  ], [localRefNAV, fireData]);

  const depletionYear = fireData.find(r => r.close === 0)?.year;
  const finalNAV      = fireData.length > 0 ? fireData[fireData.length - 1].close : 0;

  // ── Lưu ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = await getSessionUserId();
      if (!userId) {
        window.dispatchEvent(new CustomEvent('wealthos:sync-error', {
          detail: { source: 'SAA', message: 'Phiên làm việc hết hạn.', hint: 'Vui lòng đăng nhập lại.', isAuthError: true },
        }));
        return;
      }
      const rows: FundTargetRow[] = targets.map(t => ({
        fund_id:           fundId,
        asset_class_id:    t.assetClassId,
        target_weight:     t.weight,
        target_value:      t.value,
        expected_yield:    t.expYield,
        expected_cap_gain: t.expCap,
      }));
      const ok = await saveFundTargets(fundId, rows, userId, blendedYield, blendedTotal);
      if (ok) onSaved('Đã lưu hoạch định cơ cấu tài sản thành công');
    } finally {
      setSaving(false);
    }
  };

  // ── Lưu phiên bản vào localStorage ──────────────────────────────────────────
  const handleSaveScenario = () => {
    if (!scenarioName.trim()) return;
    const s: SAAScenario = {
      id: v4(), fundId, name: scenarioName.trim(), note: scenarioNote.trim(),
      refNAV: localRefNAV, targets: targets.map(t => ({ ...t })),
      isActive: false, createdAt: new Date().toISOString(),
    };
    const updated = [...scenarios, s];
    setScenarios(updated);
    persistScenarios(fundId, updated);
    setScenarioName(''); setScenarioNote(''); setShowSaveModal(false);
    setShowScenarios(true);
  };

  // ── Kích hoạt phiên bản (load + lưu DB) ──────────────────────────────────────
  const handleActivateScenario = async (scenario: SAAScenario) => {
    setTargets(scenario.targets);
    setLocalRefNAV(scenario.refNAV);
    const updated = scenarios.map(s => ({ ...s, isActive: s.id === scenario.id }));
    setScenarios(updated);
    persistScenarios(fundId, updated);
    setSaving(true);
    try {
      const userId = await getSessionUserId();
      if (!userId) {
        window.dispatchEvent(new CustomEvent('wealthos:sync-error', {
          detail: { source: 'SAA', message: 'Phiên làm việc hết hạn.', hint: 'Vui lòng đăng nhập lại.', isAuthError: true },
        }));
        return;
      }
      const rows: FundTargetRow[] = scenario.targets.map(t => ({
        fund_id: fundId, asset_class_id: t.assetClassId,
        target_weight: t.weight, target_value: t.value,
        expected_yield: t.expYield, expected_cap_gain: t.expCap,
      }));
      const sy = scenario.targets.reduce((s, t) => s + (t.weight / 100) * t.expYield, 0);
      const sc = scenario.targets.reduce((s, t) => s + (t.weight / 100) * t.expCap, 0);
      const ok = await saveFundTargets(fundId, rows, userId, sy, sy + sc);
      if (ok) onSaved(`Đã kích hoạt phiên bản "${scenario.name}"`);
    } finally { setSaving(false); }
  };

  // ── Xóa phiên bản ───────────────────────────────────────────────────────────
  const handleDeleteScenario = (id: string) => {
    const updated = scenarios.filter(s => s.id !== id);
    setScenarios(updated);
    persistScenarios(fundId, updated);
  };

  // ── Load phiên bản vào editor (không lưu DB) ─────────────────────────────────
  const handleLoadScenario = (scenario: SAAScenario) => {
    setTargets(scenario.targets);
    setLocalRefNAV(scenario.refNAV);
  };

  // ── Xuất Excel ─────────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: SAA Hoạch định ──
    const FMT_VND = '#,##0';
    const FMT_PCT = '0.00';

    const saaHeaders = [
      'Lớp tài sản', 'Tỷ trọng (%)', 'Giá trị mục tiêu (₫)',
      'Yield kỳ vọng (%)', 'Thu nhập/năm (₫)',
      'Cap Gain kỳ vọng (%)', 'Tăng giá/năm (₫)',
      'Tổng tỷ suất (%)', 'Lợi suất tuyệt đối (₫/năm)',
    ];

    const saaRows = targets.map(t => [
      t.assetClassName,
      t.weight,
      Math.round(t.value),
      t.expYield,
      Math.round(t.value * t.expYield / 100),
      t.expCap,
      Math.round(t.value * t.expCap / 100),
      t.expYield + t.expCap,
      Math.round(t.value * (t.expYield + t.expCap) / 100),
    ]);

    const summaryRow = [
      'TỔNG CỘNG',
      totalWeight,
      Math.round(totalValue),
      blendedYield,
      Math.round(localRefNAV * blendedYield / 100),
      blendedCap,
      Math.round(localRefNAV * blendedCap / 100),
      blendedTotal,
      Math.round(localRefNAV * blendedTotal / 100),
    ];

    const ws1 = XLSX.utils.aoa_to_sheet([saaHeaders, ...saaRows, [], summaryRow]);

    // Số format cho từng cột
    const vndCols  = [2, 4, 6, 8]; // C, E, G, I → 0-indexed
    const pctCols  = [1, 3, 5, 7]; // B, D, F, H
    const rowCount = saaRows.length + 3; // header + rows + empty + summary
    for (let r = 1; r < rowCount; r++) {
      vndCols.forEach(c => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (ws1[addr]) ws1[addr].z = FMT_VND;
      });
      pctCols.forEach(c => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (ws1[addr]) ws1[addr].z = FMT_PCT;
      });
    }

    ws1['!cols'] = [
      { wch: 22 }, { wch: 12 }, { wch: 20 },
      { wch: 14 }, { wch: 20 }, { wch: 16 }, { wch: 20 },
      { wch: 14 }, { wch: 22 },
    ];

    XLSX.utils.book_append_sheet(wb, ws1, 'SAA Hoạch định');

    // ── Sheet 2: FIRE Projection ──
    if (fireData.length > 0) {
      const fireHeaders = ['Năm', 'Mở đầu (₫)', 'Lợi suất (₫)', 'Rút/Tiêu dùng (₫)', 'Cuối kỳ (₫)'];
      const fireRows = fireData.map(r => [
        r.year,
        Math.round(r.open),
        Math.round(r.gain),
        Math.round(r.withdrawal),
        Math.round(r.close),
      ]);
      const ws2 = XLSX.utils.aoa_to_sheet([fireHeaders, ...fireRows]);
      const fireVndCols = [1, 2, 3, 4];
      for (let r = 1; r <= fireRows.length; r++) {
        fireVndCols.forEach(c => {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (ws2[addr]) ws2[addr].z = FMT_VND;
        });
      }
      ws2['!cols'] = [{ wch: 8 }, { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'FIRE Projection');
    }

    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `SAA_${bucket.name}_${date}.xlsx`);
  };

  const refLabel = bucket.targetValue > 0 ? 'Target NAV' : 'NAV Thực tế';

  const inputSt: React.CSSProperties = {
    width: '100%', padding: '9px 10px', borderRadius: 8, fontSize: 13,
    fontWeight: 600, outline: 'none', boxSizing: 'border-box',
    backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
    color: 'var(--tx-1)', fontVariantNumeric: 'tabular-nums',
    transition: 'border-color 0.15s',
  };

  return (
    <>
      {/* ══ Bảng SAA ══ */}
      <Card>
        <div style={{ padding: '18px 20px' }}>

          {/* ── Header + editable Target NAV ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${bucket.color}18`, border: `1px solid ${bucket.color}28` }}>
                <Target size={13} color={bucket.color} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: bucket.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Hoạch định Cơ cấu Tài sản (SAA)
              </span>
            </div>
            {/* NAV tham chiếu — có thể chỉnh sửa */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--tx-4)', whiteSpace: 'nowrap' }}>{refLabel}:</span>
              <input
                type="number"
                value={localRefNAV || ''}
                onChange={e => handleRefNAVChange(parseFloat(e.target.value) || 0)}
                step={1_000_000_000}
                min={0}
                placeholder="0"
                style={{
                  width: 180, padding: '7px 10px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                  color: bucket.color, outline: 'none', fontVariantNumeric: 'tabular-nums',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = bucket.color; }}
                onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
              />
              <span style={{ fontSize: 11, color: 'var(--tx-5)', whiteSpace: 'nowrap' }}>
                {localRefNAV > 0 ? <>= <Money value={localRefNAV} /></> : 'Chưa đặt'}
              </span>
            </div>
          </div>

          {/* ── Bảng nhập liệu ── */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any, marginBottom: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bd)' }}>
                  {[
                    { label: 'Lớp tài sản',          w: '13%' },
                    { label: 'Tỷ trọng (%)',          w: '9%',  right: true },
                    { label: 'Giá trị mục tiêu',     w: '14%', right: true },
                    { label: 'Yield kỳ vọng (%)',     w: '9%',  right: true },
                    { label: 'Thu nhập/năm (₫)',      w: '13%', right: true },
                    { label: 'Cap Gain kỳ vọng (%)',  w: '9%',  right: true },
                    { label: 'Tăng giá/năm (₫)',      w: '13%', right: true },
                    { label: 'Tổng tỷ suất',          w: '11%', right: true },
                    { label: '',                      w: '5%'  },
                  ].map(h => (
                    <th key={h.label} style={{
                      padding: '9px 10px', textAlign: h.right ? 'right' : 'left',
                      fontSize: 10, fontWeight: 700, color: 'var(--tx-4)',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      whiteSpace: 'nowrap', width: h.w,
                    }}>{h.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {targets.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: '28px 0', textAlign: 'center', color: 'var(--tx-5)', fontSize: 12, fontStyle: 'italic' }}>
                      Thêm lớp tài sản bên dưới để bắt đầu hoạch định
                    </td>
                  </tr>
                )}
                {targets.map((row, idx) => {
                  const rowTotal = row.expYield + row.expCap;
                  return (
                    <tr key={row.assetClassId} style={{ borderBottom: '1px solid var(--bd-sub)' }}>
                      {/* Lớp tài sản */}
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 9, height: 9, borderRadius: 2, backgroundColor: row.assetClassColor, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', whiteSpace: 'nowrap' }}>{row.assetClassName}</span>
                        </div>
                      </td>

                      {/* % Tỷ trọng */}
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number" min={0} max={100} step={0.1}
                            value={row.weight || ''}
                            onChange={e => updateWeight(idx, parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            style={{ ...inputSt, paddingRight: 22, textAlign: 'right' }}
                            onFocus={e => { e.target.style.borderColor = row.assetClassColor; }}
                            onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
                          />
                          <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--tx-5)', pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>

                      {/* Giá trị VND */}
                      <td style={{ padding: '10px 10px' }}>
                        <input
                          type="text" inputMode="numeric"
                          value={row.value > 0 ? Math.round(row.value).toLocaleString('vi-VN') : ''}
                          onChange={e => updateValue(idx, e.target.value)}
                          placeholder="0"
                          style={{ ...inputSt, textAlign: 'right' }}
                          onFocus={e => { e.target.style.borderColor = row.assetClassColor; }}
                          onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
                        />
                      </td>

                      {/* Yield kỳ vọng */}
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number" min={0} max={100} step={0.1}
                            value={row.expYield || ''}
                            onChange={e => updateField(idx, 'expYield', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            style={{ ...inputSt, paddingRight: 22, textAlign: 'right', color: '#fbbf24', borderColor: row.expYield > 0 ? 'rgba(251,191,36,0.3)' : 'var(--bd)' }}
                            onFocus={e => { e.target.style.borderColor = '#fbbf24'; }}
                            onBlur={e => { e.target.style.borderColor = row.expYield > 0 ? 'rgba(251,191,36,0.3)' : 'var(--bd)'; }}
                          />
                          <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--tx-5)', pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>

                      {/* Thu nhập/năm — read-only từ yield × value */}
                      <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                        {row.value > 0 && row.expYield > 0 ? (
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                            <Money value={Math.round(row.value * row.expYield / 100)} />
                          </div>
                        ) : <span style={{ color: 'var(--tx-5)', fontSize: 11 }}>—</span>}
                      </td>

                      {/* Cap Gain kỳ vọng */}
                      <td style={{ padding: '10px 10px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number" min={0} max={100} step={0.1}
                            value={row.expCap || ''}
                            onChange={e => updateField(idx, 'expCap', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            style={{ ...inputSt, paddingRight: 22, textAlign: 'right', color: '#38bdf8', borderColor: row.expCap > 0 ? 'rgba(56,189,248,0.3)' : 'var(--bd)' }}
                            onFocus={e => { e.target.style.borderColor = '#38bdf8'; }}
                            onBlur={e => { e.target.style.borderColor = row.expCap > 0 ? 'rgba(56,189,248,0.3)' : 'var(--bd)'; }}
                          />
                          <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--tx-5)', pointerEvents: 'none' }}>%</span>
                        </div>
                      </td>

                      {/* Tăng giá/năm — read-only từ capGain × value */}
                      <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                        {row.value > 0 && row.expCap > 0 ? (
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#38bdf8', fontVariantNumeric: 'tabular-nums' }}>
                            <Money value={Math.round(row.value * row.expCap / 100)} />
                          </div>
                        ) : <span style={{ color: 'var(--tx-5)', fontSize: 11 }}>—</span>}
                      </td>

                      {/* Tổng tỷ suất (read-only) */}
                      <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                        <div style={{
                          fontSize: 14, fontWeight: 700,
                          color: rowTotal >= 15 ? '#10b981' : rowTotal >= 8 ? '#fbbf24' : 'var(--tx-4)',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {rowTotal.toFixed(1)}%
                        </div>
                      </td>

                      {/* Xóa */}
                      <td style={{ padding: '10px 10px', textAlign: 'center' }}>
                        <button
                          onClick={() => removeRow(idx)}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, background: 'none', border: '1px solid var(--bd)', cursor: 'pointer', color: '#f87171', transition: 'background 0.12s' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(248,113,113,0.1)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
                          title="Xóa dòng"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* ── Footer: Tổng cộng ── */}
              {targets.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--bd)', backgroundColor: 'var(--bg-card-el)' }}>
                    <td style={{ padding: '10px 10px', fontSize: 11, fontWeight: 700, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Tổng cộng
                    </td>
                    {/* Tổng % */}
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: weightOk ? '#10b981' : '#f87171', fontVariantNumeric: 'tabular-nums' }}>
                          {totalWeight.toFixed(1)}%
                        </span>
                        {!weightOk && <AlertTriangle size={13} color="#f87171" />}
                        {weightOk  && targets.length > 0 && <CheckCircle size={13} color="#10b981" />}
                      </div>
                      {!weightOk && (
                        <div style={{ fontSize: 10, color: '#f87171', marginTop: 2, textAlign: 'right' }}>Cần bằng 100%</div>
                      )}
                    </td>
                    {/* Tổng VND */}
                    <td style={{ padding: '10px 10px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--tx-2)', fontVariantNumeric: 'tabular-nums' }}>
                      <Money value={totalValue} />
                    </td>
                    {/* Blended Yield % */}
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                        {blendedYield.toFixed(2)}%
                      </div>
                    </td>
                    {/* Thu nhập tổng (₫/năm) */}
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                      {localRefNAV > 0 ? (
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
                          <Money value={Math.round(localRefNAV * blendedYield / 100)} />
                        </div>
                      ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                    </td>
                    {/* Blended Cap Gain % */}
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#38bdf8', fontVariantNumeric: 'tabular-nums' }}>
                        {blendedCap.toFixed(2)}%
                      </div>
                    </td>
                    {/* Tăng giá tổng (₫/năm) */}
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                      {localRefNAV > 0 ? (
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8', fontVariantNumeric: 'tabular-nums' }}>
                          <Money value={Math.round(localRefNAV * blendedCap / 100)} />
                        </div>
                      ) : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                    </td>
                    {/* Hiệu suất kế hoạch + VND */}
                    <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: blendedTotal >= 15 ? '#10b981' : blendedTotal >= 8 ? '#fbbf24' : 'var(--tx-3)', fontVariantNumeric: 'tabular-nums' }}>
                        {blendedTotal.toFixed(2)}%
                      </div>
                      {localRefNAV > 0 && (
                        <div style={{ fontSize: 11, color: '#10b981', marginTop: 2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          <Money value={Math.round(localRefNAV * blendedTotal / 100)} />/năm
                        </div>
                      )}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* ── Thêm lớp tài sản ── */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: showNewCls ? 8 : 18, flexWrap: 'wrap' }}>
            <select
              value={selCls}
              onChange={e => setSelCls(e.target.value)}
              style={{
                flex: 1, minWidth: 180, padding: '10px 12px', borderRadius: 9, fontSize: 13,
                backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                color: selCls ? 'var(--tx-1)' : 'var(--tx-5)', outline: 'none', cursor: 'pointer',
              }}
            >
              <option value="">+ Chọn lớp tài sản để thêm...</option>
              {available.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={addRow}
              disabled={!selCls}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: selCls ? 'var(--bg-card-el)' : 'transparent',
                border: `1.5px solid ${selCls ? bucket.color + '60' : 'var(--bd)'}`,
                color: selCls ? bucket.color : 'var(--tx-5)',
                cursor: selCls ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s',
              }}
            >
              <Plus size={14} /> Thêm dòng
            </button>
            {onAddAssetClass && (
              <button
                onClick={() => { setShowNewCls(v => !v); setTimeout(() => newClsInputRef.current?.focus(), 50); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 14px', borderRadius: 9, fontSize: 12, fontWeight: 600,
                  background: showNewCls ? 'rgba(99,102,241,0.1)' : 'transparent',
                  border: `1.5px solid ${showNewCls ? '#6366f1' : 'var(--bd)'}`,
                  color: showNewCls ? '#6366f1' : 'var(--tx-4)',
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}
              >
                {showNewCls ? <X size={13} /> : <Plus size={13} />}
                {showNewCls ? 'Hủy' : 'Tạo lớp mới'}
              </button>
            )}
          </div>

          {/* ── Mini-form tạo lớp tài sản mới ── */}
          {showNewCls && onAddAssetClass && (
            <div style={{
              display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap',
              padding: '12px 14px', borderRadius: 10, background: 'rgba(99,102,241,0.06)',
              border: '1.5px solid rgba(99,102,241,0.2)',
            }}>
              <input
                ref={newClsInputRef}
                type="text"
                value={newClsName}
                onChange={e => setNewClsName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateClass(); }}
                placeholder="Tên lớp tài sản mới..."
                style={{
                  flex: 1, minWidth: 160, padding: '9px 12px', borderRadius: 8, fontSize: 13,
                  backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                  color: 'var(--tx-1)', outline: 'none',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 11, color: 'var(--tx-4)', whiteSpace: 'nowrap' }}>Màu:</span>
                <input
                  type="color"
                  value={newClsColor}
                  onChange={e => setNewClsColor(e.target.value)}
                  style={{ width: 34, height: 34, borderRadius: 7, border: '1.5px solid var(--bd)', cursor: 'pointer', padding: 2, background: 'var(--bg-input)' }}
                />
              </div>
              <button
                onClick={handleCreateClass}
                disabled={!newClsName.trim()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: newClsName.trim() ? '#6366f1' : 'var(--bg-card-el)',
                  border: 'none', color: newClsName.trim() ? '#fff' : 'var(--tx-5)',
                  cursor: newClsName.trim() ? 'pointer' : 'not-allowed', transition: 'background 0.15s',
                }}
              >
                <Plus size={13} /> Tạo & thêm
              </button>
            </div>
          )}

          {/* ── Nút Lưu + validation ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            {targets.length > 0 && !weightOk && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.22)' }}>
                <AlertTriangle size={13} color="#f87171" />
                <span style={{ fontSize: 12, color: '#f87171', fontWeight: 600 }}>
                  Tổng tỷ trọng đang là {totalWeight.toFixed(1)}% — cần đạt 100% trước khi lưu
                </span>
              </div>
            )}
            {targets.length > 0 && weightOk && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9, background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <CheckCircle size={13} color="#10b981" />
                <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>
                  Tỷ trọng hợp lệ · Hiệu suất kế hoạch: {blendedTotal.toFixed(2)}%/năm
                </span>
              </div>
            )}
            {targets.length === 0 && <div />}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Toggle scenarios list */}
              {scenarios.length > 0 && (
                <button
                  onClick={() => { setShowScenarios(v => !v); setShowCompare(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 9, fontSize: 12, fontWeight: 600,
                    background: showScenarios ? 'rgba(167,139,250,0.12)' : 'var(--bg-card-el)',
                    border: `1.5px solid ${showScenarios ? 'rgba(167,139,250,0.4)' : 'var(--bd)'}`,
                    color: showScenarios ? '#a78bfa' : 'var(--tx-4)', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}
                >
                  <BookOpen size={13} /> Phiên bản ({scenarios.length})
                  {showScenarios ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
              )}

              {/* Xuất Excel */}
              <button
                onClick={handleExportExcel}
                disabled={targets.length === 0}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                  background: targets.length === 0 ? 'transparent' : 'rgba(251,191,36,0.08)',
                  border: `1.5px solid ${targets.length === 0 ? 'var(--bd)' : 'rgba(251,191,36,0.3)'}`,
                  color: targets.length === 0 ? 'var(--tx-5)' : '#fbbf24',
                  cursor: targets.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}
              >
                <Download size={13} /> Xuất Excel
              </button>

              {/* Lưu phiên bản */}
              <button
                onClick={() => setShowSaveModal(v => !v)}
                disabled={targets.length === 0}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                  background: showSaveModal ? 'rgba(99,102,241,0.12)' : 'var(--bg-card-el)',
                  border: `1.5px solid ${showSaveModal ? 'rgba(99,102,241,0.4)' : targets.length > 0 ? 'rgba(99,102,241,0.3)' : 'var(--bd)'}`,
                  color: targets.length === 0 ? 'var(--tx-5)' : '#6366f1',
                  cursor: targets.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}
              >
                <Bookmark size={13} /> Lưu phiên bản
              </button>

              {/* Lưu Hoạch định (DB) */}
              <button
                onClick={handleSave}
                disabled={saving || targets.length === 0}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', borderRadius: 11, fontSize: 14, fontWeight: 700,
                  background: saving || targets.length === 0
                    ? 'var(--bd)'
                    : `linear-gradient(135deg, ${bucket.color} 0%, ${bucket.color}cc 100%)`,
                  border: 'none',
                  color: saving || targets.length === 0 ? 'var(--tx-5)' : '#fff',
                  cursor: saving || targets.length === 0 ? 'not-allowed' : 'pointer',
                  boxShadow: saving || targets.length === 0 ? 'none' : `0 4px 16px ${bucket.color}40`,
                  transition: 'opacity 0.15s', opacity: saving ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {saving ? (
                  <>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
                    Đang lưu...
                  </>
                ) : (
                  <><Save size={15} /> Lưu Hoạch định</>
                )}
              </button>
            </div>
          </div>

          {/* ── Modal: Đặt tên phiên bản ── */}
          {showSaveModal && (
            <div style={{ marginTop: 16, padding: '16px 18px', borderRadius: 12, background: 'rgba(99,102,241,0.06)', border: '1.5px solid rgba(99,102,241,0.25)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bookmark size={13} /> Lưu phiên bản hoạch định
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  type="text"
                  autoFocus
                  value={scenarioName}
                  onChange={e => setScenarioName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveScenario(); if (e.key === 'Escape') setShowSaveModal(false); }}
                  placeholder="Tên phiên bản (vd: Bảo thủ 2025, Tăng trưởng Q2...)"
                  style={{
                    padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                    color: 'var(--tx-1)', outline: 'none', width: '100%', boxSizing: 'border-box',
                  }}
                />
                <textarea
                  value={scenarioNote}
                  onChange={e => setScenarioNote(e.target.value)}
                  placeholder="Ghi chú (tùy chọn): lý do điều chỉnh, thời điểm, mục tiêu..."
                  rows={2}
                  style={{
                    padding: '9px 12px', borderRadius: 8, fontSize: 12,
                    backgroundColor: 'var(--bg-input)', border: '1.5px solid var(--bd)',
                    color: 'var(--tx-2)', outline: 'none', width: '100%', boxSizing: 'border-box',
                    resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5,
                  }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowSaveModal(false); setScenarioName(''); setScenarioNote(''); }}
                    style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, background: 'none', border: '1px solid var(--bd)', color: 'var(--tx-4)', cursor: 'pointer' }}
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleSaveScenario}
                    disabled={!scenarioName.trim()}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                      background: scenarioName.trim() ? '#6366f1' : 'var(--bd)',
                      border: 'none', color: scenarioName.trim() ? '#fff' : 'var(--tx-5)',
                      cursor: scenarioName.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <Bookmark size={12} /> Lưu phiên bản
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Danh sách phiên bản ── */}
          {showScenarios && scenarios.length > 0 && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Danh sách phiên bản đã lưu
                </span>
                {scenarios.length >= 2 && (
                  <button
                    onClick={() => setShowCompare(v => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                      background: showCompare ? 'rgba(251,191,36,0.12)' : 'var(--bg-card-el)',
                      border: `1px solid ${showCompare ? 'rgba(251,191,36,0.4)' : 'var(--bd)'}`,
                      color: showCompare ? '#fbbf24' : 'var(--tx-4)', cursor: 'pointer',
                    }}
                  >
                    <BarChart2 size={11} /> {showCompare ? 'Ẩn so sánh' : 'So sánh phiên bản'}
                  </button>
                )}
              </div>
              {scenarios.map(s => {
                const sy = s.targets.reduce((acc, t) => acc + (t.weight / 100) * t.expYield, 0);
                const sc = s.targets.reduce((acc, t) => acc + (t.weight / 100) * t.expCap, 0);
                const blended = sy + sc;
                const date = new Date(s.createdAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
                return (
                  <div key={s.id} style={{
                    padding: '12px 14px', borderRadius: 10,
                    background: s.isActive ? 'rgba(16,185,129,0.06)' : 'var(--bg-card-el)',
                    border: `1.5px solid ${s.isActive ? 'rgba(16,185,129,0.3)' : 'var(--bd)'}`,
                    transition: 'all 0.15s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          {s.isActive && (
                            <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 700, letterSpacing: '0.05em' }}>
                              ĐANG DÙNG
                            </span>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 700, color: s.isActive ? '#10b981' : 'var(--tx-1)' }}>{s.name}</span>
                        </div>
                        {s.note && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 4, lineHeight: 1.4 }}>{s.note}</div>}
                        <div style={{ fontSize: 10, color: 'var(--tx-5)', display: 'flex', gap: 10 }}>
                          <span>{date}</span>
                          <span>NAV: {(s.refNAV / 1e9).toFixed(2)} tỷ</span>
                          <span style={{ color: blended >= 15 ? '#10b981' : blended >= 8 ? '#fbbf24' : 'var(--tx-5)', fontWeight: 600 }}>
                            {blended.toFixed(2)}%/năm
                          </span>
                          <span>{s.targets.length} lớp</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleLoadScenario(s)}
                          title="Tải vào editor (chưa lưu DB)"
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '6px 11px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                            background: 'none', border: '1px solid var(--bd)',
                            color: 'var(--tx-3)', cursor: 'pointer',
                          }}
                        >
                          <Copy size={11} /> Tải
                        </button>
                        {!s.isActive && (
                          <button
                            onClick={() => handleActivateScenario(s)}
                            disabled={saving}
                            title="Kích hoạt: tải + lưu lên DB"
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '6px 11px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                              background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)',
                              color: '#10b981', cursor: saving ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <Zap size={11} /> Kích hoạt
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteScenario(s.id)}
                          title="Xóa phiên bản"
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: 7, background: 'none',
                            border: '1px solid var(--bd)', color: '#f87171', cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── So sánh phiên bản ── */}
          {showCompare && scenarios.length >= 2 && (
            <SAAScenarioCompare scenarios={scenarios} bucket={bucket} />
          )}
        </div>
      </Card>

      {/* ══════════════════════════════════════════════════════════════════
          DỰ PHÓNG DÒNG TIỀN 30 NĂM (FIRE Projection)
      ══════════════════════════════════════════════════════════════════ */}
      <Card>
        <div style={{ padding: '18px 20px' }}>

          {/* ── Header ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#10b98118', border: '1px solid #10b98128' }}>
                <TrendingUp size={13} color="#10b981" />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Dự phóng Dòng tiền 30 năm
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--tx-4)', flexWrap: 'wrap' }}>
              <span>NAV khởi đầu:</span>
              <span style={{ fontWeight: 700, color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>
                {localRefNAV > 0 ? `${(localRefNAV / 1e9).toFixed(2)} tỷ` : '—'}
              </span>
              <span style={{ color: 'var(--tx-5)' }}>·</span>
              <span>Tỷ suất kế hoạch:</span>
              <span style={{ fontWeight: 700, color: '#10b981' }}>{blendedTotal.toFixed(2)}%/năm</span>
            </div>
          </div>

          {/* ── Input Panel ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
            gap: 14, marginBottom: 20,
            padding: '16px 18px', borderRadius: 13,
            background: 'var(--bg-card-el)', border: '1px solid var(--bd)',
          }}>
            {/* Số tiền rút */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                Số tiền rút hàng năm (VND)
              </div>
              <input
                type="text" inputMode="numeric"
                value={fireWithdrawal > 0 ? fireWithdrawal.toLocaleString('vi-VN') : ''}
                onChange={e => setFireWithdrawal(Number(e.target.value.replace(/[^0-9]/g, '')) || 0)}
                placeholder="1.200.000.000"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 9,
                  fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box',
                  backgroundColor: 'var(--bg-card)', border: '1.5px solid var(--bd)',
                  color: 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', transition: 'border-color 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = '#10b981'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
              />
              <div style={{ fontSize: 11, color: '#10b981', marginTop: 4, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                = <Money value={fireWithdrawal} />/năm
              </div>
            </div>

            {/* Lạm phát */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 }}>
                Tỷ lệ lạm phát kỳ vọng (%/năm)
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="number" min={0} max={20} step={0.5}
                  value={fireInflation || ''}
                  onChange={e => setFireInflation(parseFloat(e.target.value) || 0)}
                  placeholder="4"
                  style={{
                    width: '100%', padding: '10px 40px 10px 12px', borderRadius: 9,
                    fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box',
                    backgroundColor: 'var(--bg-card)', border: '1.5px solid var(--bd)',
                    color: 'var(--tx-1)', fontVariantNumeric: 'tabular-nums', transition: 'border-color 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#fbbf24'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--bd)'; }}
                />
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--tx-5)', pointerEvents: 'none' }}>%</span>
              </div>
            </div>

            {/* Kết quả năm 30 */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {fundNAV <= 0 ? (
                <div style={{ fontSize: 11, color: 'var(--tx-5)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  Quỹ chưa có tài sản.<br />Nhập NAV vào bảng SAA để bắt đầu.
                </div>
              ) : blendedTotal <= 0 ? (
                <div style={{ fontSize: 11, color: 'var(--tx-5)', fontStyle: 'italic', lineHeight: 1.5 }}>
                  Thiết lập tỷ suất kỳ vọng<br />trong bảng SAA ở trên.
                </div>
              ) : depletionYear ? (
                <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)' }}>
                  <div style={{ fontSize: 12, color: '#f87171', fontWeight: 700 }}>⚠ Cạn vốn năm thứ {depletionYear}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 4 }}>Tăng tỷ suất hoặc giảm mức rút</div>
                </div>
              ) : (
                <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.22)' }}>
                  <div style={{ fontSize: 12, color: '#10b981', fontWeight: 700 }}>✓ Vốn tồn tại đủ 30 năm</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                    Năm 30: <strong style={{ color: '#10b981' }}><Money value={finalNAV} /></strong>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Biểu đồ đường NAV 30 năm ── */}
          {fireChartData.length > 1 ? (
            <div style={{ height: 230 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={fireChartData} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 10, fill: 'var(--chart-tick)' }}
                    tickLine={false} axisLine={false}
                    tickFormatter={v => v === 0 ? 'Nay' : `Năm ${v}`}
                    interval={4}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--chart-tick)' }}
                    tickLine={false} axisLine={false}
                    width={58}
                    tickFormatter={fmtAxis}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    {...TT}
                    formatter={(v: any) => [<Money value={v} />, 'NAV Quỹ']}
                    labelFormatter={l => l === 0 ? 'Hiện tại' : `Năm ${l}`}
                  />
                  {depletionYear && (
                    <ReferenceLine
                      x={depletionYear}
                      stroke="#f87171" strokeDasharray="4 4"
                      label={{ value: 'Cạn vốn', position: 'insideTopRight', fontSize: 10, fill: '#f87171' }}
                    />
                  )}
                  <Line
                    type="monotone" dataKey="nav"
                    stroke="#10b981" strokeWidth={2.5} dot={false}
                    activeDot={{ r: 5, fill: '#10b981', stroke: 'var(--bg-card)', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--tx-5)', fontSize: 12, fontStyle: 'italic' }}>
              Hoàn thiện bảng hoạch định SAA ở trên để xem dự phóng dòng tiền
            </div>
          )}

          {/* ── Bảng chi tiết 30 năm ── */}
          {fireData.length > 0 && (
            <div style={{ marginTop: 20, overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 560 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--bd)' }}>
                    {['Năm', 'Số dư đầu năm', 'Lợi nhuận', 'Rút tiền', 'Số dư cuối năm', 'Trạng thái'].map((h, i) => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: i === 0 ? 'center' : 'right', fontSize: 10, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fireData.map(row => {
                    const isDepleted = row.close === 0;
                    const isDecline  = !isDepleted && row.close < row.open;
                    const isGrow     = !isDepleted && row.close >= row.open;
                    const isMile     = row.year % 5 === 0;
                    const rowBg      = isDepleted ? 'rgba(248,113,113,0.08)' : isMile ? 'rgba(255,255,255,0.02)' : 'transparent';
                    const clrClose   = isDepleted ? '#f87171' : isDecline ? '#fbbf24' : '#10b981';
                    const fmt        = (v: number) => `${(v / 1e9).toFixed(2)} tỷ`;
                    return (
                      <tr key={row.year}
                        style={{ borderBottom: '1px solid var(--bd-sub)', backgroundColor: rowBg }}
                        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--bg-row-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = rowBg; }}
                      >
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span style={{ fontWeight: isMile ? 700 : 400, color: isMile ? '#10b981' : 'var(--tx-3)', fontSize: isMile ? 13 : 12 }}>
                            Năm {row.year}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--tx-2)', fontVariantNumeric: 'tabular-nums' }}>{fmt(row.open)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>+{fmt(row.gain)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: row.withdrawal > 0 ? '#f87171' : 'var(--tx-5)', fontVariantNumeric: 'tabular-nums' }}>
                          {row.withdrawal > 0 ? `-${fmt(row.withdrawal)}` : '—'}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: clrClose, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(row.close)}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          {isDepleted
                            ? <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(248,113,113,0.16)', color: '#f87171', fontWeight: 700 }}>Cạn kiệt</span>
                            : isDecline
                              ? <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(251,191,36,0.12)', color: '#fbbf24', fontWeight: 600 }}>Bào vốn</span>
                              : <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(16,185,129,0.12)', color: '#10b981', fontWeight: 600 }}>Tăng trưởng</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── AI Thẩm định ── */}
          {targets.length > 0 && (
            <>
              <div style={{ borderTop: '1px solid var(--bd)', marginTop: 20, paddingTop: 18 }}>
                <div style={{ fontSize: 11, color: 'var(--tx-5)', marginBottom: 10, textAlign: 'center', letterSpacing: '0.03em' }}>
                  Sử dụng AI để thẩm định toàn bộ chiến lược và dự phóng dòng tiền
                </div>
                <AIAppraisalButton
                  data={{
                    fundName:       bucket.name,
                    fundNAV,
                    targetNAV:      localRefNAV,
                    targets,
                    blendedYield,
                    blendedCap,
                    blendedTotal,
                    fireWithdrawal,
                    fireInflation,
                    finalNAV30:     finalNAV,
                    depletionYear,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </Card>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SAAScenarioCompare — Bảng so sánh các phiên bản SAA
// ══════════════════════════════════════════════════════════════════════════════
function SAAScenarioCompare({ scenarios, bucket }: { scenarios: SAAScenario[]; bucket: Bucket }) {
  // Tập hợp tất cả lớp tài sản xuất hiện trong các phiên bản
  const allClasses = useMemo(() => {
    const map = new Map<string, { id: string; name: string; color: string }>();
    scenarios.forEach(s => s.targets.forEach(t => {
      if (!map.has(t.assetClassId)) map.set(t.assetClassId, { id: t.assetClassId, name: t.assetClassName, color: t.assetClassColor });
    }));
    return Array.from(map.values());
  }, [scenarios]);

  const blended = (s: SAAScenario) => {
    const y = s.targets.reduce((acc, t) => acc + (t.weight / 100) * t.expYield, 0);
    const c = s.targets.reduce((acc, t) => acc + (t.weight / 100) * t.expCap, 0);
    return { yield: y, cap: c, total: y + c };
  };

  return (
    <div style={{ marginTop: 20, padding: '16px 18px', borderRadius: 12, background: 'rgba(251,191,36,0.05)', border: '1.5px solid rgba(251,191,36,0.2)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
        <BarChart2 size={12} /> So sánh {scenarios.length} phiên bản
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480, fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--bd)' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '18%' }}>
                Lớp tài sản
              </th>
              {scenarios.map(s => (
                <th key={s.id} style={{ padding: '8px 10px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: s.isActive ? '#10b981' : bucket.color, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                  {s.name}
                  {s.isActive && <span style={{ marginLeft: 4, fontSize: 8, padding: '1px 4px', borderRadius: 3, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>ACTIVE</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allClasses.map(cls => (
              <tr key={cls.id} style={{ borderBottom: '1px solid var(--bd-sub)' }}>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: cls.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-2)', whiteSpace: 'nowrap' }}>{cls.name}</span>
                  </div>
                </td>
                {scenarios.map(s => {
                  const row = s.targets.find(t => t.assetClassId === cls.id);
                  return (
                    <td key={s.id} style={{ padding: '8px 10px', textAlign: 'center' }}>
                      {row ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: cls.color, fontVariantNumeric: 'tabular-nums' }}>
                            {row.weight.toFixed(1)}%
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--tx-4)', fontVariantNumeric: 'tabular-nums' }}>
                            {(row.value / 1e9).toFixed(2)}T
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--tx-5)' }}>
                            Y:{row.expYield}% C:{row.expCap}%
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--tx-5)', fontSize: 11 }}>—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--bd)', background: 'var(--bg-card-el)' }}>
              <td style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase' }}>Tổng / Blended</td>
              {scenarios.map(s => {
                const b = blended(s);
                return (
                  <td key={s.id} style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: b.total >= 15 ? '#10b981' : b.total >= 8 ? '#fbbf24' : 'var(--tx-3)', fontVariantNumeric: 'tabular-nums' }}>
                      {b.total.toFixed(2)}%
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--tx-5)', fontVariantNumeric: 'tabular-nums' }}>
                      NAV: {(s.refNAV / 1e9).toFixed(2)}T
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--tx-5)' }}>
                      Y:{b.yield.toFixed(1)}% C:{b.cap.toFixed(1)}%
                    </div>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SAAComparison — Đối soát Actual vs Target (hiển thị trong Tab "Tổng quan")
// ══════════════════════════════════════════════════════════════════════════════
export function SAAComparison({
  targets, assetClasses, fundAssets, totalValue, fundYieldRate, fundCapGainRate, bucket,
}: {
  targets:        TargetRow[];
  assetClasses:   AssetClassMeta[];
  fundAssets:     Asset[];
  totalValue:     number;
  fundYieldRate:  number;
  fundCapGainRate: number;
  bucket:         Bucket;
}) {
  const actualByClass = useMemo(() => {
    const map: Record<string, number> = {};
    fundAssets.forEach(a => {
      const cls = assetClasses.find(c => c.id === a.assetClassId || c.name === a.assetClass);
      const val = getAssetCurrentValue(a);
      // Index kép: theo ID và theo tên — để match được cả slug lẫn UUID trong targets
      const idKey   = cls?.id   ?? a.assetClassId ?? a.assetClass ?? '';
      const nameKey = cls?.name ?? a.assetClass   ?? '';
      if (idKey)                     map[idKey]   = (map[idKey]   || 0) + val;
      if (nameKey && nameKey !== idKey) map[nameKey] = (map[nameKey] || 0) + val;
    });
    return map;
  }, [fundAssets, assetClasses]);

  const blendedTargetYield = targets.reduce((s, t) => s + (t.weight / 100) * t.expYield, 0);
  const blendedTargetCap   = targets.reduce((s, t) => s + (t.weight / 100) * t.expCap,   0);
  const blendedTargetTotal = blendedTargetYield + blendedTargetCap;
  const actualTotal        = fundYieldRate + fundCapGainRate;

  if (targets.length === 0) return null;

  const DeltaBadge = ({ delta }: { delta: number }) => {
    const abs   = Math.abs(delta);
    const color = abs <= 5 ? '#10b981' : abs <= 15 ? '#f59e0b' : '#f87171';
    const bg    = abs <= 5 ? 'rgba(16,185,129,0.1)' : abs <= 15 ? 'rgba(245,158,11,0.1)' : 'rgba(248,113,113,0.1)';
    return (
      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: bg, color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
      </span>
    );
  };

  return (
    <Card>
      <div style={{ padding: '18px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)' }}>
            <BarChart2 size={13} color="#a78bfa" />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Đối soát Thực tế vs Hoạch định (SAA)
          </span>
        </div>

        {/* NAV so sánh */}
        <div className="rsp-grid-2" style={{ gap: 12, marginBottom: 18 }}>
          {[
            { label: 'NAV Thực tế',  value: <Money value={totalValue} />, sub: `${fundAssets.length} tài sản`, color: bucket.color, accent: `${bucket.color}18` },
            {
              label: 'NAV Mục tiêu', color: '#a78bfa', accent: 'rgba(167,139,250,0.1)',
              value: bucket.targetValue > 0 ? <Money value={bucket.targetValue} /> : '—',
              sub:   bucket.targetValue > 0 ? `Đạt ${((totalValue / bucket.targetValue) * 100).toFixed(1)}%` : 'Chưa đặt mục tiêu',
            },
          ].map(kpi => (
            <div key={kpi.label} style={{ padding: '14px 16px', borderRadius: 12, background: kpi.accent, border: `1px solid ${kpi.color}25` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{kpi.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: kpi.color, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{kpi.value}</div>
              <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 4 }}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* Tiến độ lớp tài sản theo VND hoạch định */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Layers size={11} /> Tiến độ Lớp Tài sản theo Hoạch định
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {targets.map(t => {
              // Lookup theo ID trước, fallback theo tên (xử lý mismatch slug↔UUID)
              const actualVal    = actualByClass[t.assetClassId] ?? actualByClass[t.assetClassName] ?? 0;
              // % hoàn thành thực tế — KHÔNG giới hạn, cho phép > 100%
              const rawPct       = t.value > 0 ? (actualVal / t.value) * 100 : 0;
              const deltaVND     = actualVal - t.value;   // dương = vượt, âm = còn thiếu
              // % tỷ trọng thực tế (thông tin phụ)
              const actualWeight = totalValue > 0 ? (actualVal / totalValue) * 100 : 0;
              const deltaWeight  = actualWeight - t.weight;

              // ── Phân loại trạng thái để tô màu ──────────────────────────────
              // < 100%  : đang lấp đầy — màu lớp tài sản
              // 100–105%: đạt mục tiêu — xanh lá (an toàn)
              // > 105%  : vượt khung — amber/đỏ (cần chốt lời)
              const isOnTarget  = t.value > 0 && rawPct >= 100 && rawPct <= 105;
              const isOverflow  = t.value > 0 && rawPct > 105;
              const pctColor    = isOverflow ? '#f59e0b'
                                : isOnTarget ? '#10b981'
                                : t.value > 0 ? 'var(--tx-2)'
                                : 'var(--tx-5)';

              // Thanh bar luôn tối đa 100% chiều rộng, nhưng màu phản ánh tình trạng
              const barBg = isOverflow
                ? // Sọc amber-đỏ báo hiệu vượt khung
                  'repeating-linear-gradient(45deg, #f59e0b 0px, #f59e0b 6px, #d97706 6px, #d97706 12px)'
                : isOnTarget
                ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                : `linear-gradient(90deg, ${t.assetClassColor}99 0%, ${t.assetClassColor} 100%)`;

              return (
                <div key={t.assetClassId}>
                  {/* ── Dòng 1: Tên + % thực tế (không giới hạn) ── */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <div style={{ width: 9, height: 9, borderRadius: 2, backgroundColor: t.assetClassColor, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx-1)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.assetClassName}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                      {isOverflow && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', letterSpacing: '0.04em' }}>
                          VƯỢT KHUNG
                        </span>
                      )}
                      {isOnTarget && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', color: '#10b981', letterSpacing: '0.04em' }}>
                          ĐẠT MỤC TIÊU
                        </span>
                      )}
                      <span style={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: pctColor }}>
                        {t.value > 0 ? `${rawPct.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                  </div>

                  {/* ── Progress Bar — luôn max 100% width, màu theo trạng thái ── */}
                  <div style={{ height: 10, borderRadius: 5, backgroundColor: 'var(--bd)', overflow: 'hidden', marginBottom: 7 }}>
                    <div style={{
                      height: '100%', borderRadius: 5,
                      // Clip tại 100% về width nhưng giữ nguyên màu overflow
                      width: `${Math.min(rawPct, 100)}%`,
                      background: barBg,
                      transition: 'width 0.7s ease',
                    }} />
                  </div>

                  {/* ── Dòng 2: Chi tiết VND + Delta ── */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: 'var(--tx-4)', fontVariantNumeric: 'tabular-nums' }}>
                      Thực tế: <strong style={{ color: 'var(--tx-2)' }}><Money value={actualVal} /></strong>
                    </span>
                    <span style={{ color: 'var(--bd)', fontSize: 13 }}>|</span>
                    <span style={{ fontSize: 11, color: 'var(--tx-4)', fontVariantNumeric: 'tabular-nums' }}>
                      Mục tiêu: <strong style={{ color: t.assetClassColor }}>{t.value > 0 ? <Money value={t.value} /> : '—'}</strong>
                    </span>
                    {t.value > 0 && (
                      <>
                        <span style={{ color: 'var(--bd)', fontSize: 13 }}>|</span>
                        <span style={{
                          fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          color: deltaVND >= 0
                            ? (isOverflow ? '#f59e0b' : '#10b981')
                            : '#f87171',
                        }}>
                          Lệch: <Money value={deltaVND} prefix={deltaVND >= 0 ? '+' : ''} />
                        </span>
                      </>
                    )}
                    {/* Tỷ trọng thực tế — thông tin phụ, cuối dòng */}
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--tx-5)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      Tỷ trọng: {actualWeight.toFixed(1)}% / {t.weight.toFixed(1)}%
                      <span style={{
                        marginLeft: 4,
                        color: Math.abs(deltaWeight) <= 5 ? '#10b981'
                          : Math.abs(deltaWeight) <= 15 ? '#f59e0b'
                          : '#f87171',
                      }}>
                        ({deltaWeight >= 0 ? '+' : ''}{deltaWeight.toFixed(1)}pp)
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Hiệu suất: Actual vs Target */}
        <div style={{ borderTop: '1px solid var(--bd)', paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <TrendingUp size={11} /> Hiệu suất Kỳ vọng vs Thực tế
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {[
              { label: 'Yield',       actual: fundYieldRate,   target: blendedTargetYield, color: '#fbbf24' },
              { label: 'Cap Gain',    actual: fundCapGainRate, target: blendedTargetCap,   color: '#38bdf8' },
              { label: 'Tổng return', actual: actualTotal,     target: blendedTargetTotal, color: '#10b981' },
            ].map(row => (
              <div key={row.label} style={{ padding: '12px 14px', borderRadius: 11, background: 'var(--bg-card-el)', border: '1px solid var(--bd)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx-5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{row.label}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'var(--tx-4)' }}>Thực tế</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: row.color, fontVariantNumeric: 'tabular-nums' }}>{row.actual.toFixed(2)}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'var(--tx-4)' }}>Mục tiêu</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: `${row.color}99`, fontVariantNumeric: 'tabular-nums' }}>{row.target.toFixed(2)}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <DeltaBadge delta={row.actual - row.target} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
